import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { QuoteApiService } from '../services/quote-api.service';
import { QuoteApprovalApiService } from '../services/quote-approval-api.service';
import { QuoteFormModalService } from '../services/quote-form-modal.service';
import { QuoteResponse, QuoteVersionResponse, quoteRejectionReasonLabel } from '../models/quote.model';
import {
  QuoteApprovalDecision,
  QuoteApprovalResponse,
  QUOTE_STATUS_PENDING_APPROVAL,
  quoteApprovalDecidedViaLabel,
  quoteApprovalStatusChip,
  quoteApprovalStatusLabel
} from '../models/quote-approval.model';
import { QuoteStatusBadgeComponent } from '../shared/quote-status-badge/quote-status-badge.component';
import { QuoteRequestApprovalModalComponent } from '../shared/quote-request-approval-modal/quote-request-approval-modal.component';
import { QuoteApprovalDecisionModalComponent } from '../shared/quote-approval-decision-modal/quote-approval-decision-modal.component';
import { QuoteAttachmentsComponent } from '../shared/quote-attachments/quote-attachments.component';
import { QuoteDocumentComponent } from '../shared/quote-document/quote-document.component';
import { QuoteNotesComponent } from '../shared/quote-notes/quote-notes.component';
import { QuoteDocumentData, quoteDocumentFromResponse } from '../shared/quote-document/quote-document.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { AuditLogResponse } from '../../audit/models/audit.model';

type DetailTab = 'resumen' | 'documento' | 'aprobaciones' | 'versiones' | 'notas' | 'timeline' | 'adjuntos';

/**
 * Modal de detalle de una cotización (incidente #27): el estándar del CRM presenta el detalle en un
 * MODAL con pestañas, no como vista aparte. Pestañas: Resumen (rol "Info"), Documento, Aprobaciones,
 * Versiones, Notas, Línea de tiempo y Adjuntos; las pestañas pesadas cargan de forma diferida.
 *
 * La edición sigue viviendo en el wizard, que se abre POR ENCIMA de este modal vía
 * QuoteFormModalService (ambos montados en AppLayoutComponent; el wizard va después en el DOM).
 */
@Component({
  selector: 'app-quote-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    QuoteStatusBadgeComponent,
    QuoteRequestApprovalModalComponent,
    QuoteApprovalDecisionModalComponent,
    QuoteAttachmentsComponent,
    QuoteDocumentComponent,
    QuoteNotesComponent,
    AuditTimelineComponent
  ],
  templateUrl: './quote-detail-modal.component.html'
})
export class QuoteDetailModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() quoteId: number | null = null;
  @Output() close = new EventEmitter<void>();

  private readonly subs: Subscription[] = [];

  quote: QuoteResponse | null = null;
  loading = false;
  error = '';

  activeTab: DetailTab = 'resumen';

  readonly tabs: { id: DetailTab; label: string }[] = [
    { id: 'resumen', label: 'Resumen' },
    { id: 'documento', label: 'Documento' },
    { id: 'aprobaciones', label: 'Aprobaciones' },
    { id: 'versiones', label: 'Versiones' },
    { id: 'notas', label: 'Notas' },
    { id: 'timeline', label: 'Línea de tiempo' },
    { id: 'adjuntos', label: 'Adjuntos' }
  ];

  // Línea de tiempo (audit log de la cotización; requiere Manager+)
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelineForbidden = false;
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;
  private timelineLoaded = false;

  // Historial de versiones
  versions: QuoteVersionResponse[] = [];
  loadingVersions = false;
  versionsError = '';
  private versionsLoaded = false;
  restoringVersionId: number | null = null;

  downloadingPdf = false;

  // Enlace público guardado en sesión (se revela solo al publicar/rotar/enviar desde el wizard)
  publicLinkUrl: string | null = null;

  // Aprobación interna (RF-001/RF-002): historial + modales de solicitud y decisión
  approvals: QuoteApprovalResponse[] = [];
  showRequestApprovalModal = false;
  showDecisionModal = false;
  decisionToMake: QuoteApprovalDecision | null = null;

  constructor(
    private quoteApi: QuoteApiService,
    private quoteApprovalApi: QuoteApprovalApiService,
    private auditApi: AuditApiService,
    private authService: AuthService,
    private datetimeService: DatetimeService,
    private toast: ToastService,
    private confirmDialog: ConfirmDialogService,
    private formModal: QuoteFormModalService
  ) { }

  ngOnInit(): void {
    // El wizard se abre encima de este modal: al guardar, refrescar el detalle que está debajo.
    this.subs.push(this.formModal.saved$.subscribe(() => {
      if (this.isOpen && this.quoteId) {
        this.cargar(this.quoteId);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen'] && !changes['quoteId']) return;
    if (this.isOpen && this.quoteId) {
      this.cargar(this.quoteId);
    } else if (!this.isOpen) {
      this.limpiar();
    }
  }

  private get businessId(): number {
    return this.authService.getCurrentUser()?.businessId || 1;
  }

  /** Limpia el estado al cerrar: el modal se reutiliza para la siguiente cotización. */
  private limpiar(): void {
    this.quote = null;
    this.error = '';
    this.loading = false;
    this.activeTab = 'resumen';
    this.approvals = [];
    this.timelineLogs = [];
    this.timelineLoaded = false;
    this.versions = [];
    this.versionsLoaded = false;
    this.publicLinkUrl = null;
    this.showRequestApprovalModal = false;
    this.showDecisionModal = false;
    this.decisionToMake = null;
  }

  private cargar(id: number): void {
    this.loading = true;
    this.error = '';
    this.quote = null;
    this.activeTab = 'resumen';
    this.publicLinkUrl = sessionStorage.getItem(`quote_public_link_${id}`);

    // Reset de las pestañas con carga diferida.
    this.timelineLogs = [];
    this.timelineLoaded = false;
    this.timelineForbidden = false;
    this.timelineError = '';
    this.versions = [];
    this.versionsLoaded = false;
    this.versionsError = '';

    this.quoteApi.getQuoteById(id, this.businessId).subscribe({
      next: (quote) => {
        this.quote = quote;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar la cotización';
        this.loading = false;
      }
    });

    this.cargarAprobaciones(id);
  }

  cerrar(): void {
    this.close.emit();
  }

  // ---- Pestañas ----

  setTab(tab: DetailTab): void {
    this.activeTab = tab;
    if (tab === 'timeline' && !this.timelineLoaded) {
      this.loadTimeline(0);
    }
    if (tab === 'versiones' && !this.versionsLoaded) {
      this.cargarVersiones();
    }
  }

  // Historial de aprobaciones internas (más reciente primero). Error → ocultar en silencio:
  // la sección solo se muestra cuando hay entradas.
  private cargarAprobaciones(id: number): void {
    this.approvals = [];
    this.quoteApprovalApi.getApprovals(id, this.businessId).subscribe({
      next: (approvals) => {
        this.approvals = approvals;
      },
      error: () => {
        this.approvals = [];
      }
    });
  }

  // ---- Línea de tiempo (incidente #9: app-audit-timeline + AuditDisplayService) ----

  loadTimeline(page = 0): void {
    if (!this.quoteId) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelineForbidden = false;
    this.timelinePage = page;
    this.auditApi.getLogs({
      entityName: 'quote',
      idEntity: String(this.quoteId),
      page,
      size: 15
    }).subscribe({
      next: (data) => {
        this.timelineLogs = data.content ?? [];
        this.timelinePage = data.page ?? page;
        this.timelineTotalPages = data.totalPages ?? 1;
        this.timelineTotalElements = data.totalElements ?? 0;
        this.loadingTimeline = false;
        this.timelineLoaded = true;
      },
      error: (err) => {
        this.loadingTimeline = false;
        this.timelineLoaded = true;
        // 403 = rol sin permiso (Manager+): mensaje suave en vez de error rojo.
        if (err?.status === 403) {
          this.timelineForbidden = true;
          this.timelineLogs = [];
        } else {
          this.timelineError = 'No se pudo cargar la línea de tiempo.';
        }
      }
    });
  }

  get timelineEmptyMessage(): string {
    return this.timelineForbidden
      ? 'No tienes permiso para ver la línea de tiempo de auditoría.'
      : 'Aún no hay actividad registrada para esta cotización.';
  }

  // ---- Acciones ----

  // El wizard está montado DESPUÉS de este modal en el layout: se abre por encima del detalle.
  editar(): void {
    if (!this.quoteId) return;
    this.formModal.open({ quoteId: this.quoteId });
  }

  enviarAlCliente(): void {
    // Borrador + "requiere aprobación interna": el envío está bloqueado (el backend devuelve 422).
    if (!this.quoteId || this.isApprovalBlocked) return;
    this.formModal.open({ quoteId: this.quoteId, sendEmail: true });
  }

  descargarPdf(): void {
    if (!this.quoteId || !this.quote) return;
    this.downloadingPdf = true;
    this.quoteApi.downloadPdf(this.quoteId, this.businessId).subscribe({
      next: (blob) => {
        this.downloadingPdf = false;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.quote?.quoteNumber || 'cotizacion-' + this.quoteId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      },
      error: (err) => {
        this.downloadingPdf = false;
        this.toast.error({ title: 'Error', message: err.error?.message || 'Error al descargar el PDF' });
      }
    });
  }

  copiarEnlace(): void {
    if (!this.publicLinkUrl) return;
    const link = this.publicLinkUrl;
    navigator.clipboard?.writeText(link).then(
      () => this.toast.success({ title: 'Enlace copiado', message: 'Enlace de cliente copiado al portapapeles.' }),
      () => this.toast.info({ title: 'Copia manual', message: link })
    );
  }

  // ---- Aprobación interna (RF-001/RF-002) ----

  get isDraftQuote(): boolean {
    return this.quote?.statusName === 'Borrador';
  }

  get isPendingApprovalQuote(): boolean {
    return this.quote?.statusName === QUOTE_STATUS_PENDING_APPROVAL;
  }

  /**
   * Bloqueo por flag "requiere aprobación interna": en Borrador con el flag activo,
   * "Enviar al cliente" queda deshabilitado (el backend devuelve 422) y la acción
   * esperada es "Solicitar aprobación". Tras "Aprobada" se habilita normal.
   */
  get isApprovalBlocked(): boolean {
    return this.isDraftQuote && this.quote?.requiresApproval === true;
  }

  /** Solicitud PENDING vigente (si la hay). */
  get pendingApproval(): QuoteApprovalResponse | null {
    return this.approvals.find(a => a.status === 'PENDING') || null;
  }

  /** true si el usuario actual es el aprobador asignado de la solicitud PENDING. */
  get isCurrentUserApprover(): boolean {
    const pending = this.pendingApproval;
    if (!pending) return false;
    return pending.approverId === this.authService.getCurrentUser()?.id;
  }

  solicitarAprobacion(): void {
    if (!this.quoteId) return;
    this.showRequestApprovalModal = true;
  }

  cerrarSolicitarAprobacion(): void {
    this.showRequestApprovalModal = false;
  }

  // El modal ya hizo el POST y el toast: refrescar la cotización (pasó a "Pendiente de Aprobación").
  onAprobacionSolicitada(): void {
    this.showRequestApprovalModal = false;
    if (this.quoteId) {
      this.cargar(this.quoteId);
    }
  }

  abrirDecision(decision: QuoteApprovalDecision): void {
    if (!this.pendingApproval) return;
    this.decisionToMake = decision;
    this.showDecisionModal = true;
  }

  cerrarDecision(): void {
    this.showDecisionModal = false;
    this.decisionToMake = null;
  }

  // El modal ya hizo el POST y el toast: refrescar todo (estado de la cotización + historial).
  onDecidido(): void {
    this.showDecisionModal = false;
    this.decisionToMake = null;
    if (this.quoteId) {
      this.cargar(this.quoteId);
    }
  }

  approvalStatusLabel(approval: QuoteApprovalResponse): string {
    return quoteApprovalStatusLabel(approval.status);
  }

  approvalStatusChip(approval: QuoteApprovalResponse): string {
    return quoteApprovalStatusChip(approval.status);
  }

  approvalDecidedVia(approval: QuoteApprovalResponse): string {
    return quoteApprovalDecidedViaLabel(approval.decidedVia);
  }

  // ---- Historial de versiones (incidente #6) ----

  cargarVersiones(): void {
    if (!this.quoteId) return;
    this.loadingVersions = true;
    this.versionsError = '';
    this.quoteApi.getVersions(this.quoteId, this.businessId).subscribe({
      next: (versions) => {
        this.versions = versions;
        this.loadingVersions = false;
        this.versionsLoaded = true;
      },
      error: (err) => {
        this.loadingVersions = false;
        this.versionsLoaded = true;
        this.versionsError = err.error?.message || 'No se pudo cargar el historial de versiones.';
      }
    });
  }

  // Ver otra versión de la misma familia: se queda en el modal, solo cambia la cotización mostrada.
  verDetalleVersion(version: QuoteVersionResponse): void {
    if (version.id === this.quoteId) return;
    this.quoteId = version.id;
    this.cargar(version.id);
  }

  // Restaura una versión histórica: crea una nueva versión basada en esa (incidente #6).
  async restaurarVersion(version: QuoteVersionResponse): Promise<void> {
    if (version.isCurrent || this.restoringVersionId !== null) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Restaurar versión',
      message: `¿Restaurar la versión v${version.version}? Se creará una nueva versión basada en esta y la actual pasará a "Reemplazada".`,
      confirmText: 'Restaurar'
    });
    if (!ok) return;

    this.restoringVersionId = version.id;
    this.quoteApi.restoreVersion(version.id, this.businessId).subscribe({
      next: (nueva) => {
        this.restoringVersionId = null;
        this.toast.success({
          title: 'Versión restaurada',
          message: `Se creó la versión v${nueva.version} basada en la v${version.version}.`
        });
        // Sin navegar: el modal pasa a mostrar la versión recién creada.
        this.quoteId = nueva.id;
        this.cargar(nueva.id);
      },
      error: (err) => {
        this.restoringVersionId = null;
        this.toast.error({ title: 'Error', message: err.error?.message || 'No se pudo restaurar la versión.' });
      }
    });
  }

  // ---- Ciclo de vida (incidente #42) ----

  /**
   * Duraciones legibles entre hitos del ciclo de vida. Solo se incluyen las filas
   * cuyos dos extremos existen; la "decisión" es la aceptación o el rechazo.
   */
  get lifecycleRows(): { label: string; value: string }[] {
    const q = this.quote;
    if (!q) return [];
    const decision = q.datetimeApproved || q.datetimeRejected;
    const rows: { label: string; value: string }[] = [];
    this.pushDuration(rows, 'Creada → Enviada', q.datetimeCreated, q.datetimeSent);
    this.pushDuration(rows, 'Enviada → Vista', q.datetimeSent, q.datetimeViewed);
    this.pushDuration(rows, 'Vista → Decisión', q.datetimeViewed, decision);
    this.pushDuration(rows, 'Ciclo total', q.datetimeCreated, decision);
    return rows;
  }

  private pushDuration(rows: { label: string; value: string }[], label: string, fromIso?: string | null, toIso?: string | null): void {
    if (!fromIso || !toIso) return;
    const from = new Date(fromIso).getTime();
    const to = new Date(toIso).getTime();
    if (isNaN(from) || isNaN(to) || to < from) return;
    rows.push({ label, value: this.formatDuration(to - from) });
  }

  /** "35 min" si <1 h, "5 h" si <48 h, "3 d" en adelante. */
  private formatDuration(ms: number): string {
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    if (hours < 48) return `${hours} h`;
    return `${Math.floor(hours / 24)} d`;
  }

  // ---- Formato ----

  formatDateTime(iso?: string | null): string {
    return iso ? this.datetimeService.formatDateTimeShort(iso) : '—';
  }

  formatDate(iso?: string | null): string {
    return iso ? this.datetimeService.formatDateShort(iso) : '—';
  }

  formatCurrency(amount?: number | null): string {
    const symbol = this.quote?.currencySymbol || 'S/';
    const value = new Intl.NumberFormat('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    return `${symbol} ${value}`;
  }

  get expiresDate(): string | undefined {
    return this.quote?.datetimeExpires || this.quote?.validUntil;
  }

  /** Etiqueta legible del motivo de rechazo del cliente (vacía si no fue rechazada). */
  get rejectionReasonLabel(): string {
    return quoteRejectionReasonLabel(this.quote?.rejectionReason);
  }

  get taxPctLabel(): string {
    const rate = this.quote?.taxRate ?? 0;
    return `${(rate * 100).toFixed(0)}%`;
  }

  /** Datos normalizados para el componente compartido del documento legal. */
  get docData(): QuoteDocumentData | null {
    return this.quote ? quoteDocumentFromResponse(this.quote) : null;
  }
}
